import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { CheckCircle, BarChart, Users, Shield, ArrowRight, PlayCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <nav className="w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50 sticky top-0">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-xl tracking-tight text-primary">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">P</div>
            Penama Teachers Portal
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <a href="#features" className="hover:text-primary transition-colors">Features</a>
            <a href="#solutions" className="hover:text-primary transition-colors">Solutions</a>
            <a href="#pricing" className="hover:text-primary transition-colors">Pricing</a>
            <a href="#about" className="hover:text-primary transition-colors">About</a>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/student/login">
              <Button variant="outline" className="font-medium">Student Portal</Button>
            </Link>
            <Link to="/parent/login">
              <Button variant="outline" className="font-medium">Parent Portal</Button>
            </Link>
            <Link to="/coordinator/login">
              <Button variant="outline" className="font-medium">Coordinator</Button>
            </Link>
            <Link to="/login">
              <Button className="font-medium">Teacher Login</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#f0f9ff_1px,transparent_1px),linear-gradient(to_bottom,#f0f9ff_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]"></div>
        
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col gap-6 max-w-2xl">
              <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 w-fit">
                New: AI Grading Assistant 🚀
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground leading-[1.1]">
                Empowering Education in <span className="text-primary">Penama Province</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Penama Province schools comprehensive teacher portal for managing grades, assessments, 
                and student performance with ease.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/login">
                  <Button size="lg" className="w-full sm:w-auto text-base h-12 px-8">
                    Teacher Portal
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/student/login">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-base h-12 px-8">
                    Student Portal
                  </Button>
                </Link>
                <Link to="/parent/login">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-base h-12 px-8">
                    Parent Portal
                  </Button>
                </Link>
              </div>
              
              <div className="pt-8 flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-8 w-8 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs font-bold">
                       <img src={`https://randomuser.me/api/portraits/thumb/men/${i+20}.jpg`} className="rounded-full" alt="user"/>
                    </div>
                  ))}
                </div>
                <p>Serving 6 Pilot Schools across Penama Province</p>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-[600px] lg:max-w-none">
              <div className="relative rounded-2xl border bg-background/50 backdrop-blur-sm p-2 shadow-2xl ring-1 ring-muted">
                <div className="rounded-xl overflow-hidden border bg-white shadow-sm aspect-[16/10] relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_fef6b712-b8fc-4b72-95db-2ec6aee6123b/artifacts/bp6xzgnv_FB_IMG_1770833764351~2.jpg" 
                    alt="Manaro Volcano - Vanuatu Natural Beauty" 
                    className="w-full h-full object-cover"
                    style={{
                      filter: 'brightness(1.1) contrast(1.15) saturate(1.3)',
                      transform: 'scale(1.05)',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  {/* Gradient overlay for depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
                </div>
                
                {/* Floating Cards */}
                <div className="absolute -left-8 bottom-12 bg-white p-4 rounded-xl shadow-xl border max-w-[200px] hidden md:block animate-in slide-in-from-left-5 duration-700 delay-300">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                      <CheckCircle className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold">Attendance</p>
                      <p className="text-[10px] text-muted-foreground">Class 3B - Science</p>
                    </div>
                  </div>
                  <div className="w-full bg-secondary/20 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-secondary w-[92%] h-full rounded-full"></div>
                  </div>
                  <p className="text-right text-[10px] font-bold mt-1 text-secondary">92% Present</p>
                </div>

                <div className="absolute -right-6 top-12 bg-white p-4 rounded-xl shadow-xl border max-w-[180px] hidden md:block animate-in slide-in-from-right-5 duration-700 delay-500">
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart className="h-4 w-4 text-primary" />
                    <p className="text-xs font-bold">Class Performance</p>
                  </div>
                  <div className="flex items-end justify-between gap-1 h-12">
                    {[40, 65, 45, 80, 60].map((h, i) => (
                      <div key={i} className="w-4 bg-primary/20 rounded-t-sm" style={{height: `${h}%`}}>
                        <div className={`w-full rounded-t-sm ${i === 3 ? 'bg-primary' : 'bg-primary/60'}`} style={{height: '100%'}}></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-muted/30" id="features">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold tracking-tight mb-4">Everything you need to run a class</h2>
            <p className="text-muted-foreground text-lg">
              Powerful tools designed to reduce administrative burden and increase student success rates.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: "Student Management",
                desc: "Track attendance, behavior, and progress for every student in one secure profile."
              },
              {
                icon: CheckCircle,
                title: "Smart Grading",
                desc: "Create rubrics, grade assignments, and sync directly with your gradebook automatically."
              },
              {
                icon: Shield,
                title: "Secure Communication",
                desc: "Message students and parents safely without sharing personal contact information."
              }
            ].map((feature, i) => (
              <Card key={i} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="pt-6">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12 mt-auto bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 font-bold text-xl tracking-tight text-primary mb-4">
                <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">R</div>
                RTClassManaSys
              </div>
              <p className="text-sm text-muted-foreground">
                Empowering educators worldwide with modern tools for modern classrooms.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Features</li>
                <li>Pricing</li>
                <li>Integrations</li>
                <li>Changelog</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>About</li>
                <li>Careers</li>
                <li>Blog</li>
                <li>Contact</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Privacy</li>
                <li>Terms</li>
                <li>Security</li>
              </ul>
            </div>
          </div>
          <div className="text-center text-sm text-muted-foreground pt-8 border-t">
            © 2025 RTClassManaSys Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
